import { logError } from "@/logging/logging";
import { useToast } from "@faro-lotv/flat-ui";
import {
  isNetworkConnectionError,
  validateNonEmptyString,
  validateNotNullishObject,
} from "@faro-lotv/foundation";
import { isValid } from "@faro-lotv/ielement-types";
import {
  ApiResponseError,
  ApiValidationError,
  MutationRejectionCode,
  ProjectApiError,
  getMutationErrorProblemsMessages,
  isApiResponseMutationErrorMessage,
  isMergeConflictError,
  isMutationResult,
} from "@faro-lotv/service-wires";
import {
  PropsWithChildren,
  ReactNode,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useMemo,
  useState,
  type JSX,
} from "react";
import {
  ErrorBoundary,
  useErrorHandler as reactErrorBoundaryHandler,
} from "react-error-boundary";
import { AppErrorDialog } from "./app-error-dialog";
import { AppErrorPage } from "./app-error-page";

/** The error information to display inside the dialog */
export type ErrorDialogData = {
  /** The error dialog title */
  title: string | JSX.Element;
  /** The error raised */
  error: unknown;
};

/** A type that allows to handle errors in different ways. */
export type ErrorHandlers = {
  /** Error handler that displays a page with error information. */
  handleErrorWithPage(error: Error | unknown): void;

  /** Error handler that displays an error toast with given title and a message. */
  handleErrorWithToast(data: ErrorDialogData): void;

  /** Error handler that displays a modal dialog with given title, message and error button. */
  handleErrorWithDialog(data: ErrorDialogData): void;
};

/**
 * This type is needed because we cannot put the 'handleErrorWithPage' callback in the
 * context, it must be created anew for each call to 'useErrorHandlers()'
 */
type ErrorHandlersInContext = {
  /** Error handler that displays an error toast with given title and a message. */
  handleErrorWithToast(data: ErrorDialogData): void;

  /** Error handler that displays a modal dialog with given title, message and error button. */
  handleErrorWithDialog(data: ErrorDialogData): void;
};

/**
 * A context to expose the required error handling functions to be used inside components
 */
export const ErrorHandlersContext = createContext<
  ErrorHandlersInContext | undefined
>(undefined);

/**
 * @returns The error handlers
 */
export function useErrorHandlers(): ErrorHandlers {
  // The callback below cannot stay in the context, it must be created fresh
  // every time calling 'reactErrorBoundaryHandler()'.
  const ebHandler = reactErrorBoundaryHandler();
  const ctx = useContext(ErrorHandlersContext);
  if (ctx) {
    return {
      handleErrorWithPage: ebHandler,
      ...ctx,
    };
  }
  // While running tests, this context may not exist.
  return {
    handleErrorWithPage: ebHandler,
    handleErrorWithToast: (data: ErrorDialogData) => {
      logError(data.error);
      console.error(parseErrorMessage(data.error));
    },
    handleErrorWithDialog: (data: ErrorDialogData) => {
      logError(data.error);
      console.error(parseErrorMessage(data.error));
    },
  };
}

/**
 * @param error Error of unknown type
 * @returns An error message for an unknown error object
 */
export function parseErrorMessage(error: unknown): ReactNode {
  if (error instanceof ProjectApiError) {
    let message: string | undefined = undefined;
    if (typeof error.body === "object") {
      message = error.body?.data?.title;

      switch (error.body.error) {
        case "mutation_failed":
        case "merge_conflict": {
          const mutationResults = error.body.data;

          if (Array.isArray(mutationResults)) {
            // Only the failed mutations contain useful information
            const messages = mutationResults
              .filter((mutationResult) => mutationResult.status === "failure")
              .flatMap(parseUnknownMutationResultMessage)
              .filter(isValid);

            if (messages.length <= 1) {
              message = messages[0];
            } else if (messages.length > 1) {
              return (
                <ErrorList
                  message={`${error.status}: Multiple errors occurred`}
                  details={messages}
                />
              );
            }
          }
          break;
        }
      }
    }
    return `${error.status}: ${message ?? "An unknown error occurred"}`;
  } else if (error instanceof ApiResponseError) {
    if (isMergeConflictError(error)) {
      return "A merge conflict occurred. Please restart the workflow.";
    }

    const messages = getMutationErrorProblemsMessages(error).join("\n");
    if (messages) return messages;

    if (error.body?.data?.title) return error.body.data.title;
  } else if (error instanceof ApiValidationError) {
    return "Encountered invalid data";
  } else if (isNetworkConnectionError(error)) {
    return "Network connection issue: please try again";
  } else if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  } else if (typeof error === "string" || isValidElement(error)) {
    return error;
  }
  return "An unknown error occurred";
}

function parseUnknownMutationResultMessage(
  mutationResult: unknown,
): string | string[] | undefined {
  if (isMutationResult(mutationResult)) {
    if (!mutationResult.message) return;

    // The partialSuccessDisabled error only happens where there is already another error in an earlier mutation.
    // In this case, we don't want to show this message as it is not useful to the user.
    if (
      mutationResult.rejectionCode ===
      MutationRejectionCode.partialSuccessDisabled
    ) {
      return;
    }

    const messageAsJson = tryJsonParse(mutationResult.message);
    if (isApiResponseMutationErrorMessage(messageAsJson)) {
      return messageAsJson.Problems.map(
        (problem) => `${messageAsJson.ObjectInfo}: ${problem.ErrorMessage}`,
      );
    }

    return mutationResult.message ?? undefined;
  }

  if (
    validateNotNullishObject(mutationResult, "mutationResult") &&
    validateNonEmptyString(mutationResult, "message")
  ) {
    // validated above
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return mutationResult.message as string;
  }
}

/**
 * Tries to parse a JSON object, but doesn't throw if it fails.
 *
 * @returns The parsed JSON object, or undefined if parsing failed.
 * @param inputString the string to parse as JSON
 */
function tryJsonParse(inputString: string): unknown {
  try {
    return JSON.parse(inputString);
  } catch {
    // Ignore errors, if input is not valid JSON
  }
}

interface ErrorListProps {
  /** The main message to display above the list */
  message: string;

  /** The individual messages to display in the list */
  details: string[];
}

/**
 * @returns a formatted list of errors
 */
function ErrorList({ message, details }: ErrorListProps): JSX.Element {
  return (
    <>
      {message}
      <ul>
        {details.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
    </>
  );
}

/**
 * @returns A context providing to all its children an error handling framework
 * such that any exceptions thrown can be handled via an ErrorBoundary component
 * (breaking the rendering of children components), or via a message in a modal
 * dialog (keeping rendering and state of children components) or via other means.
 */
export function ErrorHandlingContext({
  children,
}: PropsWithChildren): JSX.Element {
  const [dialogData, setDialogData] = useState<ErrorDialogData | undefined>(
    undefined,
  );

  const { openToast } = useToast();

  const handleErrorWithToast = useCallback(
    (data: ErrorDialogData) =>
      openToast({
        variant: "error",
        title: data.title,
        message: parseErrorMessage(data.error),
      }),
    [openToast],
  );

  const handleErrorWithDialog = useCallback(
    (data: ErrorDialogData) => setDialogData(data),
    [setDialogData],
  );

  const closeErrorDialog = useCallback(
    () => setDialogData(undefined),
    [setDialogData],
  );

  const value = useMemo<ErrorHandlersInContext>(
    () => ({
      handleErrorWithToast,
      handleErrorWithDialog,
    }),
    [handleErrorWithDialog, handleErrorWithToast],
  );

  return (
    <ErrorHandlersContext value={value}>
      <ErrorBoundary FallbackComponent={AppErrorPage} onError={logError}>
        {children}
      </ErrorBoundary>
      {dialogData && (
        <AppErrorDialog
          title={dialogData.title}
          message={parseErrorMessage(dialogData.error)}
          onClose={closeErrorDialog}
        />
      )}
    </ErrorHandlersContext>
  );
}

/**
 * Wrap an error handler to ignore errors caused by aborting a signal.
 *
 * @param handleError The function to handle non-abort errors.
 * @param signal The abort signal to check for abort errors.
 *    If not provided, the function will check for abort errors on a best-effort basis.
 * @returns The function to handle errors, but ignoring abort errors.
 */
export function ignoreSignalAbort<Err, Ret>(
  handleError: (error: Err) => Ret,
  signal?: AbortSignal,
): (error: Err) => Ret | undefined {
  return (error: Err) => {
    // If a signal is provided, check it explicitly
    if (signal?.aborted) {
      return;
    }
    // Otherwise, fall back to a best-effort check
    if (error instanceof DOMException && error.name === "AbortError") {
      return;
    }
    return handleError(error);
  };
}
